import { Directive, OnDestroy } from '@angular/core';
import { Params } from '@angular/router';
import { ServiceInjector } from '@core/services/service-injector.service';
import { ResponseArray, ResponseDefault, ResponseError, ResponseObject } from '@shared/interfaces/http.interface';
import { BooleanObject } from '@shared/interfaces/objects.interface';
import { FacadeService } from '@shared/services/facade.service';
import { Subscription } from 'rxjs';

@Directive()
export abstract class ComponentHelper implements OnDestroy {
  protected service: FacadeService;
  protected subscriptions: Subscription = new Subscription();
  public loaders: BooleanObject = {};
  public componentError: boolean;

  constructor() {
    this.service = ServiceInjector.injector.get(FacadeService);
  }

  protected setLoader(key: string | number, value: boolean) {
    this.loaders[key] = value;
  }

  protected setComponentError(status: boolean = true): void {
    this.componentError = status;
  }

  protected setRedirectWithLastPageQueryParams(route: string): void {
    try {
      this.service.router.navigate([route], { queryParams: this.getLastPageQueryParamsFromUrl() });
    } catch (error) {
      console.error('Invalid redirect with last page query params.')
    }
  }

  protected getCurrentQueryParams(): Params | undefined {
    try {
      const urlSearchParams: URLSearchParams = new URLSearchParams(window.location.search);
      const queryParams: Params = {};

      urlSearchParams.forEach((val, key) => queryParams[key] = val);

      if (Object.keys(queryParams).length) {
        return queryParams;
      }

      return undefined;
    } catch (error) {
      console.error('Invalid current query params: ', error);

      return undefined;
    }
  }

  protected getLastPageQueryParams(): Params | undefined {
    try {
      const queryParams: Params | undefined = this.getCurrentQueryParams() || {};

      if (Object.keys(queryParams).length) {
        const lastPageQueryParams = new URLSearchParams(queryParams);

        return { lastPageQueryParams }
      }

      return undefined;
    } catch (error) {
      console.error('Probably invalid current query params: ', error);
      return undefined;
    }
  }

  protected getLastPageQueryParamsFromUrl(): Params | undefined {
    try {
      const urlSearchParams: URLSearchParams = new URLSearchParams(window.location.search);
      const lastPageQueryParams = urlSearchParams.get('lastPageQueryParams');

      if (lastPageQueryParams) {
        const urlParams: URLSearchParams = new URLSearchParams(lastPageQueryParams);
        const paramsObject: Params = {};

        urlParams.forEach((value, key) => {
          paramsObject[key] = value;
        });

        return paramsObject;
      }

      return undefined;
    } catch (error) {
      console.error('Invalid last page query params: ', error);

      return undefined;
    }
  }

  protected getResponseData<T>(response: ResponseArray<T>): T[] | undefined;
  protected getResponseData<T>(response: ResponseObject<T>): T | undefined;
  protected getResponseData<T>(response: ResponseObject<T> | ResponseArray<T>): T | T[] | undefined {
    const { data, error, errors } = { ...response };
    this.setComponentError(false);

    if (data && Array.isArray(data)) {
      return (data as T[]);
    } else if (data) {
      return (data as T);
    } else if (error || errors) {
      this.setComponentError(true);
    }

    return undefined;
  }

  public onResponseSuccess(response: ResponseDefault, successMessage?: string): Promise<null> {
    return new Promise<null>((resolve => {
      this.service.toastr.success(response?.data?.message || successMessage);
      resolve(null);
    }));
  }

  public onResponseError(response: ResponseError = ({} as ResponseError), errorMessage?: string): Promise<null> {
    return new Promise<null>((resolve => {
      this.service.toastr.error(response?.error || errorMessage);
      resolve(null);
    }));
  }

  onResponseSaved(response: ResponseDefault): Promise<null> {
    return new Promise<null>(resolve => {
      this.service.toastr.success(response?.data?.message || 'Pomyślnie zapisano dane.');
      resolve(null);
    });
  }

  onResponseSavedError(response: ResponseError = ({} as ResponseError)): Promise<null> {
    return new Promise<null>(resolve => {
      this.service.toastr.error(response.error || `Wystąpił błąd (${response.status.toString() || '-'}).`);
      resolve(null);
    });
  }

  onResponseDeleted(response: ResponseDefault): Promise<null> {
    return new Promise<null>(resolve => {
      const message: string = response && response.data && response.data.message ? response.data.message : 'Pomyślnie usunięto element';

      this.service.toastr.success(message);
      resolve(null);
    });
  }

  onResponseDeletedError(response: ResponseError = ({} as ResponseError)): Promise<null> {
    return new Promise<null>(resolve => {
      this.service.toastr.error(response.error || `Wystąpił błąd (${response.status.toString() || '-'}).`);
      resolve(null);
    });
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
